import { Fragment, useState } from "react";
import { isReadOnlyRole } from "@bitacorapro/api-types";
import { QueryBoundary } from "../components/QueryBoundary";
import { Skel } from "../components/Skel";
import { SkelTable } from "../components/skeleton/SkelTable";
import { useRevokeShareBundle, useShareBundleAccesses, useShareBundles } from "../lib/api/queries";
import { useSession } from "../lib/auth/useSession";
import { canManageShareBundles } from "../lib/capabilities";

// The table's column headers — static copy, so the skeleton renders the
// same header row the data lands under.
const BUNDLE_HEADERS = ["", "Destinatario", "Observaciones", "Creado", "Vence", ""];

// Durable list of active share bundles + revoke — lives outside the
// drawer (drawers close on navigation, this needs to persist).
export function EnlacesCompartidos() {
  const bundles = useShareBundles();
  const revoke = useRevokeShareBundle();
  const [confirming, setConfirming] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const session = useSession();
  const role = session?.organization.role;
  // Read-only roles (ADR-0164) never get the revoke control, whatever
  // canManageShareBundles admits: core-api answers 403 ROLE_READ_ONLY.
  const canManage = canManageShareBundles(role) && !isReadOnlyRole(role ?? "");

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header__lead">
          <div className="page-header__eyebrow">Postventa</div>
          <h1 className="page-header__title">Enlaces compartidos</h1>
          <p className="page-header__sub">
            Enlaces públicos activos generados desde Observaciones. Revocar un enlace impide nuevos
            accesos de inmediato — pero si el proveedor ya descargó o abrió una foto antes de la
            revocación, ese archivo puede seguir siendo accesible para él hasta que ADR-0107
            (revocación efectiva de evidencia) cierre esta brecha.
          </p>
        </div>
      </div>

      <QueryBoundary
        query={bundles}
        label="los enlaces compartidos"
        skeleton={
          <div className="panel">
            <SkelTable rows={8} cols={6} headers={BUNDLE_HEADERS} />
          </div>
        }
        isEmpty={(data) => data.bundles.length === 0}
        empty={
          <div className="panel">
            <div className="panel__body--flush">
              <div className="empty">
                <div className="empty__title">Sin enlaces activos</div>
                <div className="empty__sub">Genera un enlace desde la lista de Observaciones.</div>
              </div>
            </div>
          </div>
        }
      >
        {(data) => (
          <div className="panel">
            <div className="panel__body--flush">
              <table className="dtable">
                <thead>
                  <tr>
                    {BUNDLE_HEADERS.map((h, i) => (
                      <th key={i}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.bundles.map((b) => (
                    <Fragment key={b.id}>
                      <tr>
                        <td>
                          {canManage && (
                            <button
                              type="button"
                              className="btn btn--ghost btn--sm"
                              aria-expanded={expanded === b.id}
                              onClick={() => setExpanded(expanded === b.id ? null : b.id)}
                            >
                              {expanded === b.id ? "Ocultar" : "Historial"}
                            </button>
                          )}
                        </td>
                        <td>
                          <span className="cell-strong">{b.recipientLabel ?? "Sin nombre"}</span>
                        </td>
                        <td>{b.itemCount}</td>
                        <td className="cell-meta">
                          {new Date(b.createdAt).toLocaleDateString("es-CL")}
                        </td>
                        <td className="cell-meta">
                          {b.expiresAt
                            ? new Date(b.expiresAt).toLocaleDateString("es-CL")
                            : "Nunca"}
                        </td>
                        <td>
                          {canManage &&
                            (confirming === b.id ? (
                              <button
                                type="button"
                                className="btn btn--danger btn--sm"
                                disabled={revoke.isPending}
                                onClick={() => {
                                  void revoke.mutateAsync(b.id).finally(() => setConfirming(null));
                                }}
                              >
                                ¿Seguro? Ya no funcionará
                              </button>
                            ) : (
                              <button
                                type="button"
                                className="btn btn--ghost btn--sm"
                                onClick={() => setConfirming(b.id)}
                              >
                                Revocar
                              </button>
                            ))}
                        </td>
                      </tr>
                      {canManage && expanded === b.id && (
                        <tr>
                          <td colSpan={6}>
                            <AccessHistory bundleId={b.id} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </QueryBoundary>
    </div>
  );
}

function AccessHistory({ bundleId }: { bundleId: string }) {
  const accesses = useShareBundleAccesses(bundleId);

  // A section inside the already-loaded table: no status announcement of
  // its own and an error that never moves focus.
  return (
    <QueryBoundary
      query={accesses}
      label="el historial de accesos"
      kind="section"
      skeleton={
        <div className="proof-list">
          <div className="proof-list__item">
            <div className="proof-list__label">
              <Skel variant="text" width="40%" />
            </div>
            <div className="proof-list__photos">
              <Skel width={72} height={72} />
              <Skel width={72} height={72} />
            </div>
          </div>
        </div>
      }
    >
      {(data) => (
        <>
          {data.proof.length > 0 && (
            <div className="proof-list">
              {data.proof.map((p) => (
                <div key={p.shareLinkId} className="proof-list__item">
                  <div className="proof-list__label">
                    {p.findingElement ?? p.findingAreaName ?? "Observación"}
                    {p.submittedAt && (
                      <span className="cell-meta">
                        {" · "}
                        {new Date(p.submittedAt).toLocaleString("es-CL")}
                      </span>
                    )}
                  </div>
                  {p.note && <p className="proof-list__note">{p.note}</p>}
                  {p.photos.length > 0 && (
                    <div className="proof-list__photos">
                      {p.photos.map((photo) => (
                        // ph-no-capture: photo.url is a presigned R2 URL (org
                        // prefix + signature). Keeps PostHog autocapture from
                        // shipping it as attr__href / $external_click_url and
                        // replay from rendering it.
                        <a
                          key={photo.url}
                          href={photo.url}
                          target="_blank"
                          rel="noreferrer"
                          className="proof-list__photo ph-no-capture"
                        >
                          <img src={photo.url} alt="Evidencia enviada por el proveedor" />
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          {/* La telemetría de apertura se retiró (decisión 10 del handoff del
          panel, 2026-09-16): el producto NO le dice a la inmobiliaria si
          el contratista abrió su enlace, ni cuándo, ni desde qué IP.
          Ninguna superficie debe insinuarlo — un «vio el enlace» invita
          a leerlo como compromiso, y no lo es. La señal legítima de que
          algo está trabado es «asignada hace N días sin resolverse», que
          se deriva del hilo.

          `share_link_accesses` sigue escribiéndose: es registro de
          acceso, y su valor es forense, no de gestión. Lo que se quitó
          es la superficie, no el dato. */}
        </>
      )}
    </QueryBoundary>
  );
}
